import { useCallback, useEffect, useMemo, useState } from 'react';
import { Contract } from '../../../../models/Entities/Contracts/Contract';
import { connect } from 'react-redux';
import {
  Field,
  Spinner,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerProps
} from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

// own functions
import * as ownFunctions from './ContractSelectorFunctions';


export const ContractsSelectorComponent = (props: ownFunctions.Props) => {
  const { t } = useTranslation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filterQuery, setFilterQuery] = useState<string>('');

  // Sync external pre-selection
  useEffect(() => {
    if (props.ContractsSelected && props.ContractsSelected.length > 0) {
      setSelectedIds(props.ContractsSelected.map((id) => id.toString()));
    }
  }, [props.ContractsSelected]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = useCallback((_event, data) => {
    setFilterQuery('');

    if (data.value === 'no-options') {
      setSelectedIds([]);
      props.onSelectedContract([]);
      return;
    }

    let newSelectedOptions: string[];

    // Detect if it's a removal (dismiss) or addition operation
    const isRemoving = data.selectedOptions.length < selectedIds.length;

    if (isRemoving) {
      newSelectedOptions = data.selectedOptions;
    } else if (props.MultiselectEnable) {
      newSelectedOptions = data.selectedOptions;
    } else {
      newSelectedOptions = [data.value];
    }

    setSelectedIds(newSelectedOptions);

    const newContracts = (props.Contracts ?? []).filter((c: Contract) =>
      newSelectedOptions.includes(c.id.toString())
    ) || [];
   
    props.onSelectedContract(newContracts);
  }, [selectedIds, props.MultiselectEnable, props.Contracts, props.onSelectedContract]);

  const onInputChange = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      setFilterQuery(e.currentTarget.value);
    }, []);

  const tagPickerOptions = (props.Contracts ?? []).filter((c: Contract) =>
      !selectedIds.includes(c.id.toString()) &&
      c.contractNumber.toLowerCase().includes(filterQuery.toLowerCase())
  );

  const selectedContracts = useMemo(() => {
    return (props.Contracts ?? []).filter((c: Contract) =>
      selectedIds.includes(c.id.toString())
    );
  }, [props.Contracts, selectedIds]);

  return (
    <>
      {!props.isLoading && props.isLoadingSuccessfully && (
        <Field label={t('ContractSelector.label', 'Seleccione un Contrato')}>
          <TagPicker
            onOptionSelect={onOptionSelect}
            selectedOptions={selectedIds}
            disabled={props.disabled}
          >
            <TagPickerControl>
              <TagPickerGroup aria-label={t('ContractSelector.selectedAria', 'Contratos seleccionados')}>
                {selectedContracts.map((contract, index) => (
                  <Tag key={contract.id + '-' + index} shape="rounded" value={contract.id.toString()}>
                    {contract.contractNumber}
                  </Tag>
                ))}
              </TagPickerGroup>
              <TagPickerInput
                aria-label={t('ContractSelector.inputAria', 'Seleccione un contrato')}
                onKeyUp={onInputChange}
              />
            </TagPickerControl>
            <TagPickerList>
              {tagPickerOptions.length > 0 ? (
                tagPickerOptions.map((contract: Contract) => (
                  <TagPickerOption
                    value={contract.id.toString()}
                    key={contract.id}
                    text={contract.contractNumber}
                  >
                    {contract.contractNumber}
                  </TagPickerOption>
                ))
              ) : (
                <TagPickerOption value="no-options" text="">
                  {t('ContractSelector.noResults', 'No se encontró contrato')}
                </TagPickerOption>
              )}
            </TagPickerList>
          </TagPicker>
          {props.noteText && <small>{props.noteText}</small>}
        </Field>
      )}
      {props.isLoading && <Spinner size="extra-tiny" label={t('ContractSelector.loading', 'Cargando contratos...')} />}
    </>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(ContractsSelectorComponent as any);
